import React, { useState, useRef, useEffect } from "react";
import { FleetrifyBgs } from "../../../assets/video";
import { Link } from "react-router-dom";
import { FLEETRIFYROUTEPATH } from "../../../routes/routesPath";
import { RiAccountCircleFill as RiAccountCircleFillRaw } from "react-icons/ri";
import { ArrowRight, Camera } from "lucide-react";
import CarouselCard from "../../../components/Carousel";
import { useCompanyProfile } from "../../../controllers/useCompanyProfile";

const RiAccountCircleFill = RiAccountCircleFillRaw as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

interface Option {
  id: string;
  name: string;
}

interface DropdownProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  maxVisibleItems: number;
  disabled?: boolean;
}

const CustomDropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  maxVisibleItems,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setOpen(false);
      setSearch("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.id === value);

  const filteredOptions = options.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        className={`w-full bg-gray-100 border border-gray-500 text-[#4F4F4F] rounded-md px-5 py-3 text-[16px] text-left flex justify-between items-center ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }`}
        onClick={() => {
          setOpen((prev) => !prev);
          setSearch("");
        }}
      >
        {selectedOption ? selectedOption.name : placeholder}

        <svg className="w-5 h-5 text-[#4F4F4F]" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 12a1 1 0 01-.707-.293l-4-4a1 1 0 011.414-1.414L10 9.586l3.293-3.293a1 1 0 111.414 1.414l-4 4A1 1 0 0110 12z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          className="absolute z-50 w-full bg-white border border-gray-300 rounded-md shadow-lg"
          style={{
            bottom: "100%",
            top: "auto",
          }}
        >
          <div className="p-2 border-b">
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-md text-sm"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <ul
            className="overflow-y-auto"
            style={{
              maxHeight: `${maxVisibleItems * 30}px`,
              minHeight: `${maxVisibleItems * 30}px`,
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((o) => (
                <li
                  key={o.id}
                  className="px-5 py-2 hover:bg-blue-100 cursor-pointer text-[#4F4F4F]"
                  onClick={() => {
                    onChange(o.id);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  {o.name}
                </li>
              ))
            ) : (
              <li className="px-5 py-2 text-gray-400 italic">No results found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const IndividualProfile: React.FC = () => {
  const slides = [
    { title: "Welcome to Fleetrify", description: "Login to manage logistics and fleet operations" },
    { title: "Real-time Fleet Insights", description: "Get live updates and analytics on every vehicle in your fleet" },
    { title: "Simplify Logistics Management", description: "Automate routing, tracking, and reporting — all in one platform" },
  ];

  const {
    companyName,
    setCompanyName,
    companyAddress,
    setCompanyAddress,
    companyPhone,
    setCompanyPhone,
    selectedCountry,
    setSelectedCountry,
    selectedState,
    setSelectedState,
    countries,
    states,
    countriesLoading,
    statesLoading,
    companyLogo,
    setCompanyLogo,
    saveStep1,
  } = useCompanyProfile();

  const renderForm = () => (
    <div className="w-full max-w-xl bg-white lg:bg-transparent lg:p-0 lg:shadow-none rounded-lg p-5 shadow-lg">
      <h2 className="text-center text-[24px] font-bold mt-4 text-[#143887]">INDIVIDUAL PROFILE</h2>
      <p className="text-center text-[16px] font-bold text-[#4F4F4F] mb-5">
        Set up your individual’s profile
      </p>

      <div className="flex justify-center items-center flex-col gap-2 mb-4">
        <input
          type="file"
          accept="image/*"
          id="companyLogoInput"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) setCompanyLogo(file);
          }}
        />

        <div className="relative w-[128.5px] h-[128.5px]">
          {companyLogo ? (
            <img
              src={companyLogo instanceof File ? URL.createObjectURL(companyLogo) : companyLogo}
              alt="Company Logo"
              className="w-[128.5px] h-[128.5px] rounded-full object-cover cursor-pointer"
              onClick={() => document.getElementById("companyLogoInput")?.click()}
            />
          ) : (
            <RiAccountCircleFill
              className="w-[128.5px] h-[128.5px] text-[#143887] hover:text-blue-800 cursor-pointer"
              onClick={() => document.getElementById("companyLogoInput")?.click()}
            />
          )}

          <button
            type="button"
            onClick={() => document.getElementById("companyLogoInput")?.click()}
            className="absolute bottom-1 right-1 bg-[#143887] text-white p-1.5 rounded-full border-2 border-white hover:bg-blue-800 transition"
            aria-label="Upload company logo"
          >
            <Camera size={14} />
          </button>
        </div>

        <p className="text-xs text-gray-500">Upload your company logo</p>
      </div>

      <div className="mt-2 space-y-4 w-full">
        <div>
          <label className="text-[16px] font-bold text-[#000000]">Business Name</label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Enter Business name"
            className="w-full border border-[#1438874D] mt-2 mb-2 text-[#4F4F4F] bg-white rounded-md px-5 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="text-[16px] font-bold text-[#000000]">Business Address</label>
          <input
            type="text"
            value={companyAddress}
            onChange={(e) => setCompanyAddress(e.target.value)}
            placeholder="Enter Business address"
            className="w-full bg-white rounded-md border mt-2 mb-2 text-[#4F4F4F] border-[#1438874D] px-5 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="text-[16px] font-bold text-[#000000]">Phone Number</label>
          <input
            type="text"
            value={companyPhone}
            onChange={(e) => setCompanyPhone(e.target.value)}
            placeholder="Enter phone number"
            className="w-full bg-white rounded-md border mt-2 mb-2 text-[#4F4F4F] border-[#1438874D] px-5 py-3 text-[16px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex flex-col md:flex-row gap-6 w-full">
          <div className="flex flex-col flex-1 min-w-[200px]">
            <p className="text-[#000000] font-bold text-[16px]">Country</p>
            <CustomDropdown
              options={countries || []}
              value={selectedCountry}
              onChange={setSelectedCountry}
              placeholder={countriesLoading ? "Loading..." : "Select Country"}
              maxVisibleItems={8}
            />
          </div>
          <div className="flex flex-col flex-1 min-w-[200px]">
            <p className="text-[#000000] font-bold text-[16px]">State</p>
            <CustomDropdown
              options={states || []}
              value={selectedState}
              onChange={setSelectedState}
              placeholder={statesLoading ? "Loading..." : "Select State"}
              maxVisibleItems={5}
              disabled={!selectedCountry || statesLoading}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-5 w-full mt-5 mb-3">
        <Link
          to={FLEETRIFYROUTEPATH.INDIVIDUALPROFILESETUP}
          className="flex-1 bg-[#143887] text-center text-white py-4 rounded-md hover:bg-blue-800"
          onClick={() => saveStep1()}
        >
          Next
        </Link>

        {/* <Link
          to={FLEETRIFYROUTEPATH.LOGIN}
          className="flex-1 bg-white text-blue-700 border border-[#143887] py-3 rounded-md hover:bg-[#143887] hover:text-white flex items-center justify-center gap-2"
        >
          <span>Skip</span>
          <ArrowRight className="w-4 h-4" />
        </Link> */}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      <div className="relative w-full lg:w-[45%] h-screen lg:sticky top-0 flex-shrink-0">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={FleetrifyBgs} type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-black opacity-50 z-10"></div>

        <div>
          <CarouselCard slides={slides} />
        </div>

        <div className="absolute inset-0 flex justify-center items-center z-30 px-5 md:px-10 lg:hidden">
          {renderForm()}
        </div>
      </div>

      <div className="hidden lg:flex lg:w-[55%] lg:p-10 lg:items-center lg:justify-center bg-white">
        {renderForm()}
      </div>
    </div>
  );
};

export default IndividualProfile;
